<template>
  <div :class="$style.base">
    <EmptySubscriptionPage
      v-if="!hasFeatureFlag('module-register')"
      :text="{
        title: $t('upgrade_subscription_page.register.checkout.title'),
        description: $t(
          'upgrade_subscription_page.register.checkout.description',
          brandName(),
        ),
        btnPrimary: $t('upgrade_subscription_page.upgrade_btn')
      }"
      :moreLink="{
        text: $t('upgrade_subscription_page.register.checkout.learn_more'),
        url: helpCenterArticleUrl(
          $t('upgrade_subscription_page.register.checkout.learn_more_url'),
        )
      }"
      imageName="register"
      v-test="'empty-page'"
    />

    <BaseSpinner v-else-if="loadingData" />

    <Layout v-else>
      <template #left>
        <Browser
          v-if="showMobileBrowser || $screen !== 's'"
          @hideMobileBrowser="showMobileBrowser = false"
          @createNote="showCreateNoteModal = true"
          v-test="'register-browser'"
        />
      </template>
      <template #right>
        <Order
          v-if="!showMobileBrowser || $screen !== 's'"
          @showMobileBrowser="showMobileBrowser = true"
          v-test="'register-order'"
        />
      </template>
    </Layout>

    <OpenDrawerModal
      v-if="showOpenDrawerModal"
      @close="showOpenDrawerModal = false"
      @success="goToPayment"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'RegisterBuilder',
};
</script>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import EmptySubscriptionPage from '@/modules/_shared/subscription/EmptySubscriptionPage.vue';
import { useRegisterBrowserStore } from '@/modules/register/builder/browser/store';
import OpenDrawerModal from '@/modules/register/modals/open-drawer/index.vue';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useUserStore } from '@/stores/user';
import { TransactionType } from '@/types';
import unleash from '@/unleash';
import { useRegisterOrderStore } from '../stores/order';
import { setAppointmentData } from './actions/appointment';
import { setCustomerData } from './actions/customer';
import { setOrderData } from './actions/order';
import Browser from './browser/index.vue';
import Layout from './Layout.vue';
import Order from './order/index.vue';
import { useOrderSubmit } from './order/submit';

const { hasFeatureFlag } = useUserStore();
const { showMobileBrowser } = storeToRefs(useRegisterBrowserStore());
type ValidationCallback = (valid: boolean) => void;
let sidebarTrigger: ((callback: ValidationCallback) => void) | null = null;
provide(
  'registerSidebarTrigger',
  (fn: (callback: ValidationCallback) => void) => {
    sidebarTrigger = fn;
  },
);
provide('triggerSidebarValidation', (callback: ValidationCallback) => {
  sidebarTrigger ? sidebarTrigger(callback) : callback(true);
});
const { order, reset } = useRegisterOrderStore();
const route = useRoute();
const router = useRouter();
const { goToPayment } = useOrderSubmit();
const { showOpenDrawerModal } = storeToRefs(useRegisterStore());

const { loadData: loadAppointment, loading: loadingAppointment } =
  setAppointmentData();
const { loadData: loadOrder, loading: loadingOrder } = setOrderData();
const { loadData: loadCustomer, loading: loadingCustomer } = setCustomerData();
const loadingData = computed(
  () => loadingAppointment.value || loadingOrder.value || loadingCustomer.value,
);

const orderIsNotEditable = computed(() => {
  const hasPosTransaction = !!order.transactions.find(
    (t) => t.type === TransactionType.Pos,
  );

  return (
    !order.permissions?.editOrderItems ||
    (hasPosTransaction && route.query.action !== 'modify')
  );
});

if (route.query.direct) {
  reset();
}

const missingFiskaly = computed(
  () =>
    unleash.isEnabled('EnableFiskalySign') &&
    hasFeatureFlag('compliant-register') &&
    hasFeatureFlag('fiskaly-sign') &&
    !hasFeatureFlag('fiskaly-sign-enabled'),
);

const checkRedirect = () => {
  if (route.query.direct && !missingFiskaly.value) {
    router.replace({
      name: 'register-payment',
      query: {
        direct: true,
      },
    });
  }
};

if (route.query.orderId) {
  loadOrder().then(() => {
    checkRedirect();
  });
} else if (route.query.appointmentId) {
  loadAppointment().then(() => {
    checkRedirect();
  });
} else if (route.query.customerId) {
  loadCustomer();
} else if (order.id && orderIsNotEditable.value) {
  reset();
}

if (!route.query.direct && Object.keys(route.query).length) {
  router.replace({ query: {} });
}

showMobileBrowser.value = false;

watch(
  route,
  (value) => {
    if (
      value.name !== 'register-builder' &&
      value.name !== 'register-payment'
    ) {
      showMobileBrowser.value = true;
    }
  },
  {
    immediate: true,
  },
);

const { fetchFavorites } = useRegisterBrowserStore();
fetchFavorites();
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}
</style>
